import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

/*
 * The only button in the application.
 *
 * Two rules are built in rather than remembered:
 *
 *   - **Yellow is a background, never a label colour.** `outline` and `ghost`
 *     take `bark-700`, not `honey-400`. The prototype's outline buttons are
 *     yellow-on-white at 1.34:1, and one of them is the "Add photo" control a
 *     cleaner taps ten times a job (P3, P23). Every pair here is in
 *     `TOKEN_PAIRS` and checked by tests/unit/design-tokens.test.ts.
 *   - **44px minimum, in every size.** `sm` is visually smaller but keeps the
 *     hit area, which is what design-system.md §9.6 asks for: shrink the look,
 *     not the target.
 */

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium ' +
    'transition-colors disabled:pointer-events-none disabled:opacity-50 ' +
    '[&_svg]:size-[18px] [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-honey-400 text-ink-900 hover:bg-honey-300 active:bg-honey-500',
        outline: 'border border-bark-700 bg-white text-bark-700 hover:bg-honey-50',
        ghost: 'text-bark-700 hover:bg-honey-50',
        // Destructive actions only. `status-priority-text` is 6.47:1 on white.
        danger: 'bg-status-priority-text text-white hover:opacity-90',
        // Looks like a link, behaves like a button. Still 44px tall.
        link: 'text-bark-700 underline underline-offset-4 hover:text-bark-900',
      },
      size: {
        // The default. 44px is the floor, not the target.
        md: 'min-h-touch px-4 text-label',
        // Visually smaller, same hit area — the padding does the work.
        sm: 'min-h-touch px-3 text-meta',
        lg: 'min-h-[52px] px-6 text-label',
        // Square, for icon-only buttons. These always need an aria-label.
        icon: 'min-h-touch min-w-touch p-0',
      },
      full: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md', full: false },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Render as the child element instead of a `<button>` — for links that look like buttons. */
  asChild?: boolean;
}

export function Button({
  className,
  variant,
  size,
  full,
  asChild = false,
  type = 'button',
  ...props
}: ButtonProps) {
  const Component = asChild ? Slot : 'button';

  return (
    <Component
      // Explicit, because an unspecified button inside a form submits it. That
      // is the default nobody expects and it is a real source of accidental
      // submissions.
      {...(asChild ? {} : { type })}
      className={cn(buttonVariants({ variant, size, full }), className)}
      {...props}
    />
  );
}
